import React, { useState, useRef } from 'react';

interface DropzoneComposerProps {
  onSendMessage: (text: string, file: File | null) => void;
  isSubmitting?: boolean;
  initialPrompt?: string;
  errorMessage?: string | null;
}

export default function DropzoneComposer({
  onSendMessage,
  isSubmitting = false,
  initialPrompt = '',
  errorMessage,
}: DropzoneComposerProps) {
  const [text, setText] = useState(initialPrompt);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Sync initialPrompt changes if passed from parent suggestions
  React.useEffect(() => {
    if (initialPrompt) {
      setText(initialPrompt);
    }
  }, [initialPrompt]);

  const validateAndSetFile = (file: File) => {
    const ext = file.name.split('.').pop()?.toLowerCase();
    if (ext === 'png') {
      setLocalError('PNG format is not supported by the parser. Please upload PDF, JPG, or JPEG.');
      setSelectedFile(null);
      return;
    }
    if (!['pdf', 'jpg', 'jpeg'].includes(ext || '')) {
      setLocalError('Unsupported file type. Please upload a PDF, JPG, or JPEG document.');
      setSelectedFile(null);
      return;
    }
    setLocalError(null);
    setSelectedFile(file);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      validateAndSetFile(e.target.files[0]);
    }
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragOver(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      validateAndSetFile(e.dataTransfer.files[0]);
    }
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!text.trim() && !selectedFile) return;
    onSendMessage(text, selectedFile);
    setText('');
    setSelectedFile(null);
    setLocalError(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  const activeError = localError || errorMessage;

  return (
    <div>
      {/* Error alert pill */}
      {activeError && (
        <div className="mb-3 px-4 py-2.5 rounded-xl bg-rose-50 dark:bg-rose-950/40 border border-rose-200 dark:border-rose-800 text-xs font-mono text-rose-700 dark:text-rose-300 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <svg className="w-4 h-4 fill-current flex-shrink-0 text-rose-500" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
            <span>{activeError}</span>
          </div>
          <button
            type="button"
            onClick={() => setLocalError(null)}
            className="text-rose-500 hover:text-rose-700 p-1"
          >
            ✕
          </button>
        </div>
      )}

      <div
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        className={`rounded-2.5xl bg-white dark:bg-boxdark p-3.5 shadow-card border-2 border-dashed transition-all duration-200 ${
          isDragOver
            ? 'border-brand-vib bg-[#F4F8FF] dark:bg-brand-900/30'
            : selectedFile
            ? 'border-brand-green/60 dark:border-brand-green/40'
            : 'border-brand-vib/35 dark:border-strokedark'
        }`}
      >
        {/* Selected file preview pill */}
        {selectedFile && (
          <div className="flex items-center justify-between gap-2 px-3 py-2 mb-2 rounded-xl bg-brand-green-tint dark:bg-emerald-950/40 border border-brand-green/40 dark:border-emerald-800 text-xs font-mono text-emerald-900 dark:text-emerald-300">
            <div className="flex items-center gap-2 truncate">
              <svg className="w-4 h-4 text-emerald-600 fill-current flex-shrink-0" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z" clipRule="evenodd" />
              </svg>
              <span className="font-semibold truncate">{selectedFile.name}</span>
              <span className="text-emerald-600/80 dark:text-emerald-400/80">
                ({(selectedFile.size / 1024).toFixed(1)} KB)
              </span>
            </div>
            <button
              type="button"
              onClick={() => {
                setSelectedFile(null);
                if (fileInputRef.current) fileInputRef.current.value = '';
              }}
              className="text-emerald-700 hover:text-emerald-900 dark:text-emerald-400 dark:hover:text-emerald-200 p-1"
            >
              ✕
            </button>
          </div>
        )}

        <form onSubmit={handleSubmit} className="flex flex-col gap-2">
          <div className="flex items-end gap-2.5">
            {/* File attachment trigger (PDF, JPG, JPEG only - no PNG) */}
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileChange}
              accept=".pdf,.jpg,.jpeg"
              className="hidden"
              id="invoice-file-input"
            />
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              title="Attach invoice document (PDF, JPG, JPEG)"
              className={`flex h-11 w-11 items-center justify-center rounded-xl transition-colors duration-150 flex-shrink-0 ${
                selectedFile
                  ? 'bg-brand-green-tint text-emerald-700 dark:bg-emerald-950/60 dark:text-emerald-300 border border-brand-green/40'
                  : 'bg-brand-blue-tint text-brand-vib hover:bg-brand-vib hover:text-white dark:bg-brand-900/40 dark:text-brand-300 dark:hover:bg-brand-vib dark:hover:text-white border border-brand-vib/20'
              }`}
            >
              <svg className="w-5 h-5 fill-current" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M8 4a3 3 0 00-3 3v4a5 5 0 0010 0V7a1 1 0 112 0v4a7 7 0 11-14 0V7a5 5 0 0110 0v4a3 3 0 11-6 0V7a1 1 0 012 0v4a1 1 0 102 0V7a3 3 0 00-3-3z" clipRule="evenodd" />
              </svg>
            </button>

            {/* Prompt textarea */}
            <textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              onKeyDown={handleKeyDown}
              maxLength={3000}
              rows={2}
              placeholder={
                selectedFile
                  ? "Document attached. Add custom instructions or press Send to start parsing..."
                  : "Ask AI Agent to parse, audit tax, or drop a PDF/JPG invoice here..."
              }
              className="w-full resize-none bg-transparent px-3 py-2 text-sm text-brand-navy dark:text-white placeholder-brand-soft focus:outline-none"
            />

            {/* Digital Green Send Button */}
            <button
              type="submit"
              disabled={isSubmitting || (!text.trim() && !selectedFile)}
              className="flex h-11 w-11 items-center justify-center rounded-xl bg-brand-green text-brand-navy hover:bg-[#0fd48d] disabled:opacity-40 disabled:hover:bg-brand-green transition-colors duration-150 flex-shrink-0 shadow-sm font-bold"
            >
              {isSubmitting ? (
                <svg className="w-5 h-5 animate-spin fill-current" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
              ) : (
                <svg className="w-5 h-5 fill-current" viewBox="0 0 24 24">
                  <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z" />
                </svg>
              )}
            </button>
          </div>

          {/* Footer meta */}
          <div className="flex items-center justify-between pt-2 border-t border-brand-line/60 dark:border-strokedark text-[11px] font-mono text-brand-soft dark:text-bodydark2 px-1">
            <span className="flex items-center gap-1">
              <svg className="w-3.5 h-3.5 fill-current" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zM6.293 6.707a1 1 0 010-1.414l3-3a1 1 0 011.414 0l3 3a1 1 0 01-1.414 1.414L11 5.414V13a1 1 0 11-2 0V5.414L7.707 6.707a1 1 0 01-1.414 0z" clipRule="evenodd" />
              </svg>
              Drop PDF / JPG / JPEG (PNG unsupported)
            </span>
            <span>{text.length} / 3,000</span>
          </div>
        </form>
      </div>
    </div>
  );
}
